import { intl, UUID } from '@beelday/common'
import { FormEvent, useLayoutEffect, useRef } from 'react'
import {
	Action,
	CharCount,
	CtaBar,
	Eyebrow,
	Hint,
	Option,
	Options,
	Question,
	Screen,
	SendError,
	TextInput,
} from './primitives'

type ChoiceOption = { id: UUID; text: string }

type MultipleChoiceProps = {
	question: string
	options: ChoiceOption[]
	multiple: boolean
	selected: UUID[]
	sending: boolean
	failed: boolean
	onToggle: (id: UUID) => void
	onSend: () => void
}

export const MultipleChoiceView = ({
	question,
	options,
	multiple,
	selected,
	sending,
	failed,
	onToggle,
	onSend,
}: MultipleChoiceProps): JSX.Element => (
	<Screen $withCta data-testid="in_person.mobile.poll.multiple_choice">
		<Eyebrow>
			<intl.Translate>in_person.mobile.poll.eyebrow</intl.Translate>
		</Eyebrow>
		<Question>{question}</Question>
		<Options role={multiple ? 'group' : 'radiogroup'}>
			{options.map(option => (
				<Option
					key={option.id}
					multiple={multiple}
					selected={selected.includes(option.id)}
					disabled={sending}
					onClick={() => onToggle(option.id)}
				>
					{option.text}
				</Option>
			))}
		</Options>
		{multiple ? (
			<Hint>
				<intl.Translate>in_person.mobile.poll.multiple_hint</intl.Translate>
			</Hint>
		) : null}
		{failed ? <SendError /> : null}
		<CtaBar>
			<Action busy={sending} disabled={!selected.length} onClick={onSend}>
				<intl.Translate>in_person.mobile.send</intl.Translate>
			</Action>
		</CtaBar>
	</Screen>
)

type WordCloudProps = {
	question: string
	value: string
	maxLength: number
	sending: boolean
	failed: boolean
	/**
	 * Put the cursor in the field as it appears - for "add another answer",
	 * where the tap already says the participant is about to type. Done in a
	 * layout effect, still inside that tap, so iOS opens the keyboard.
	 */
	focusOnMount?: boolean
	onChange: (value: string) => void
	onSend: () => void
}

export const WordCloudView = ({
	question,
	value,
	maxLength,
	sending,
	failed,
	focusOnMount = false,
	onChange,
	onSend,
}: WordCloudProps): JSX.Element => {
	const translate = intl.useTranslate()
	const inputRef = useRef<HTMLInputElement>(null)

	useLayoutEffect(() => {
		if (focusOnMount) {
			inputRef.current?.focus()
		}
	}, [focusOnMount])
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (value.trim() && !sending) {
			onSend()
		}
	}

	return (
		<Screen $withCta data-testid="in_person.mobile.poll.word_cloud">
			<Eyebrow>
				<intl.Translate>in_person.mobile.poll.word_cloud</intl.Translate>
			</Eyebrow>
			<Question id="in-person-word-cloud-question">{question}</Question>
			<form onSubmit={submit}>
				<TextInput
					ref={inputRef}
					type="text"
					value={value}
					maxLength={maxLength}
					enterKeyHint="send"
					aria-labelledby="in-person-word-cloud-question"
					placeholder={translate(
						'in_person.mobile.poll.word_cloud.placeholder'
					)}
					readOnly={sending}
					onChange={event => onChange(event.target.value.slice(0, maxLength))}
				/>
				<CharCount aria-hidden>
					{value.length}/{maxLength}
				</CharCount>
			</form>
			{failed ? <SendError /> : null}
			<CtaBar>
				<Action busy={sending} disabled={!value.trim()} onClick={onSend}>
					<intl.Translate>in_person.mobile.send</intl.Translate>
				</Action>
			</CtaBar>
		</Screen>
	)
}
